import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { PageHeader, BrutalCard } from "../components/brutal";
import { useAuth } from "../lib/auth";
import { Lock, Share2, Download, Instagram, X } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";

function BadgeInstagramCard({ badge, user, forwardedRef }) {
  return (
    <div
      ref={forwardedRef}
      style={{
        width: 1080,
        height: 1080,
        background: "linear-gradient(160deg, #FFD600 0%, #FF5C00 55%, #FF3AC5 100%)",
        padding: 72,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
      }}
    >
      <div
        style={{
          position: "absolute", inset: 0,
          backgroundImage: "radial-gradient(circle at 10px 10px, rgba(9,9,11,0.08) 1px, transparent 0)",
          backgroundSize: "22px 22px",
        }}
      />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 2 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 64, height: 64, background: "#09090B", border: "4px solid #09090B",
            borderRadius: 18, display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "8px 8px 0 0 #ffffff",
          }}>
            <span style={{ fontFamily: "'Outfit'", fontWeight: 900, fontSize: 36, color: "#FFD600" }}>⚡</span>
          </div>
          <div style={{ color: "#09090B" }}>
            <div style={{ fontFamily: "'Outfit'", fontWeight: 900, fontSize: 38, lineHeight: 1 }}>ReferPro</div>
            <div style={{ fontSize: 16, textTransform: "uppercase", letterSpacing: 4, opacity: 0.85 }}>
              Achievement unlocked
            </div>
          </div>
        </div>
        <div style={{
          background: "#09090B", color: "#fff", padding: "12px 22px",
          border: "4px solid #09090B", borderRadius: 100, fontWeight: 800, fontSize: 18,
          letterSpacing: 2, textTransform: "uppercase",
        }}>
          🏆 Badge
        </div>
      </div>

      <div style={{
        marginTop: 40, background: "#FFFFFF", border: "6px solid #09090B",
        borderRadius: 36, boxShadow: "16px 16px 0 0 #09090B",
        padding: 56, flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
        textAlign: "center", justifyContent: "center", zIndex: 2,
      }}>
        {badge.icon_url && (
          <img
            src={badge.icon_url}
            alt=""
            style={{ width: 360, height: 360, objectFit: "contain" }}
          />
        )}
        <div style={{
          fontFamily: "'Outfit'", fontWeight: 900, fontSize: 88, lineHeight: 1, marginTop: 20,
          letterSpacing: "-0.03em",
        }}>
          {badge.name}
        </div>
        <div style={{ fontSize: 28, marginTop: 16, color: "#52525B", fontWeight: 500, maxWidth: 760 }}>
          {badge.description}
        </div>
        <div style={{
          marginTop: 32, display: "inline-flex", alignItems: "center", gap: 18,
          background: "#FFD600", border: "4px solid #09090B", borderRadius: 100,
          padding: "14px 28px", boxShadow: "6px 6px 0 0 #09090B",
        }}>
          <img
            src={user?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(user?.name || "U")}`}
            alt=""
            style={{ width: 52, height: 52, borderRadius: 100, border: "3px solid #09090B" }}
          />
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: 3, fontWeight: 800, opacity: 0.7 }}>
              Earned by
            </div>
            <div style={{ fontFamily: "'Outfit'", fontWeight: 900, fontSize: 28, lineHeight: 1 }}>{user?.name}</div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 28, color: "#09090B", fontWeight: 800, textAlign: "center", fontSize: 22, zIndex: 2 }}>
        #MadeAtReferPro
      </div>
    </div>
  );
}

function ShareDialog({ badge, user, onClose }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const exportPng = async () => {
    if (!cardRef.current) return null;
    return toPng(cardRef.current, {
      cacheBust: true, pixelRatio: 1, width: 1080, height: 1080,
      fetchRequestInit: { mode: "cors" },
    });
  };

  const download = async () => {
    setBusy(true);
    try {
      const dataUrl = await exportPng();
      if (!dataUrl) return;
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `referpro-badge-${(badge.name || "badge").replace(/\s+/g, "-").toLowerCase()}.png`;
      a.click();
      toast.success("Downloaded — share it on Instagram!");
    } catch {
      toast.error("Couldn't export image. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    setBusy(true);
    try {
      const dataUrl = await exportPng();
      if (!dataUrl) return;
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "referpro-badge.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: badge.name, text: `Just unlocked "${badge.name}" on ReferPro ⚡` });
      } else {
        await download();
        return;
      }
    } catch {
      toast.error("Couldn't share. Try download instead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="badge-share-modal"
    >
      <div className="brutal bg-white max-w-lg w-full p-5 md:p-6 my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-bold text-neutral-600">Share your win</div>
            <h3 className="font-display font-black text-2xl leading-tight">{badge.name}</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 border-2 border-black rounded-xl flex items-center justify-center" data-testid="badge-share-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="brutal bg-white overflow-hidden">
          <div style={{ width: "100%", aspectRatio: "1 / 1", position: "relative", overflow: "hidden" }}>
            <div
              style={{
                position: "absolute", top: 0, left: 0, width: 1080, height: 1080,
                transform: "scale(var(--s, 0.45))", transformOrigin: "top left",
              }}
              ref={(el) => {
                if (!el) return;
                const w = el.parentElement?.clientWidth || 540;
                el.style.setProperty("--s", String(w / 1080));
              }}
            >
              <BadgeInstagramCard badge={badge} user={user} forwardedRef={cardRef} />
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <button onClick={share} disabled={busy} className="btn-brutal btn-primary" data-testid="badge-share-btn">
            <Instagram className="w-4 h-4" /> Share
          </button>
          <button onClick={download} disabled={busy} className="btn-brutal btn-white" data-testid="badge-download-btn">
            <Download className="w-4 h-4" /> Download PNG
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BadgesPage() {
  const { user } = useAuth();
  const [badges, setBadges] = useState([]);
  const [sharing, setSharing] = useState(null);
  const canShare = user?.permissions?.allow_badge_share !== false;

  useEffect(() => {
    api.get("/badges").then((r) => setBadges(r.data));
  }, []);

  return (
    <div>
      <PageHeader
        title="Badges & Achievements"
        subtitle="Milestones you've unlocked along the way — and a few still to earn."
      />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {badges.map((b) => (
          <BrutalCard
            key={b.id}
            className={`p-6 text-center flex flex-col ${b.earned ? "bg-brand-yellow" : "bg-white"}`}
            data-testid={`badge-${b.code}`}
          >
            <div className={`mx-auto w-40 h-40 flex items-center justify-center ${b.earned ? "" : "opacity-40 grayscale"}`}>
              {b.icon_url ? (
                <img src={b.icon_url} alt={b.name} className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full bg-neutral-200 rounded-2xl border-2 border-black" />
              )}
            </div>
            <h3 className="font-display font-black text-2xl mt-4">{b.name}</h3>
            <p className="text-sm text-neutral-700 mt-1 flex-1">{b.description}</p>
            <div className={`mt-4 chip mx-auto ${b.earned ? "bg-brand-green" : "bg-white"}`}>
              {b.earned ? "UNLOCKED" : (<><Lock className="w-3 h-3" /> Locked</>)}
            </div>
            {b.earned && canShare && (
              <button
                onClick={() => setSharing(b)}
                className="btn-brutal btn-white w-full mt-4 text-sm"
                data-testid={`badge-share-${b.code}`}
              >
                <Share2 className="w-4 h-4" /> Share to Instagram
              </button>
            )}
            {b.earned && !canShare && (
              <div className="text-[11px] text-neutral-600 mt-4">
                Sharing disabled in <a href="/app/settings" className="underline font-bold">Settings</a>
              </div>
            )}
          </BrutalCard>
        ))}
      </div>

      {sharing && <ShareDialog badge={sharing} user={user} onClose={() => setSharing(null)} />}
    </div>
  );
}
